import React from "react";
import { Button } from "../../UI/Button";
import { TableCell } from "../../UI/TableCell";
import { TableRow } from "../../UI/TableRow";
import { Typography } from "../../UI/Typography";
import { Grid } from "../../UI/Grid";
import { ModalProvider } from "../../contexts/ModalContext";
import { IntegrationKineticEditButton } from "./IntegrationKineticEditButton";
import { useIntegrationContext } from "./IntegrationProvider";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import { deleteIntegrationsKinetic } from "../../services/IntegrationApi";
import useAuth from "../../routes/login/AuthContext";

interface IProps {
  id: string;
  l1Id: number;
  l2Id: number;
  roomsyncFloorPlan: string;
  instanceId: string;
  enabledAccess: string;
  mappingType: string;
  yearOfStudy: string;
  studentType: string;
  pathway: number;
  importFieldSelection?: "STUDENT_TYPE" | "PATHWAY_ID" | "BOTH" | "NONE";
  showAll?: boolean;
}

export const IntegrationTableKineticBodyRow: React.FC<IProps> = ({
  id,
  l1Id,
  l2Id,
  roomsyncFloorPlan,
  instanceId,
  enabledAccess,
  mappingType,
  yearOfStudy,
  studentType,
  pathway,
  importFieldSelection,
}) => {
  const { isSuperAdmin, getAuthToken } = useAuth();
  const { loadIntegrations } = useIntegrationContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const getImportFieldsLabel = (value?: string) => {
    switch (value) {
      case "BOTH":
        return "Both";
      case "STUDENT_TYPE":
        return "Student Type";
      case "PATHWAY_ID":
        return "Pathway ID";
      case "NONE":
        return "None";
      default:
        return "-";
    }
  };

  const handleDelete = async () => {
    // TODO: Replace with proper confirmation dialog when available
    // For now, proceeding with deletion directly
    setIsDeleting(true);
    try {
      const res = await deleteIntegrationsKinetic(
        selectedNetworkId,
        parseInt(id),
        await getAuthToken()
      );

      if (res && res.ok) {
        await loadIntegrations(selectedNetworkId);
        showSuccessSnackbar("Kinetic integration deleted successfully");
      } else {
        showErrorSnackbar("Failed to delete Kinetic integration");
      }
    } catch (error) {
      showErrorSnackbar("An error occurred while deleting Kinetic integration");
      // eslint-disable-next-line no-console
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2">{roomsyncFloorPlan}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{instanceId}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{enabledAccess}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{mappingType}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{yearOfStudy}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{getImportFieldsLabel(importFieldSelection)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{studentType || "-"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{pathway || "-"}</Typography>
      </TableCell>
      {isSuperAdmin() && (
        <TableCell>
          <Grid container spacing={1} wrap="nowrap">
            <Grid item>
              <ModalProvider>
                <IntegrationKineticEditButton
                  id={id}
                  l1Id={l1Id}
                  l2Id={l2Id}
                  roomsyncFloorPlan={roomsyncFloorPlan}
                  instanceId={instanceId}
                  enabledAccess={enabledAccess}
                  mappingType={mappingType}
                  yearOfStudy={yearOfStudy}
                  studentType={studentType}
                  pathway={pathway}
                  importFieldSelection={importFieldSelection}
                />
              </ModalProvider>
            </Grid>
            <Grid item>
              <Button
                appearance="danger"
                onClick={handleDelete}
                isDisabled={isDeleting}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </TableCell>
      )}
    </TableRow>
  );
};
